import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, FlaskConical, ShieldAlert } from "lucide-react";
import Logo from "./Logo";

const NAV = [
    { label: "Catalog", href: "#catalog" },
    { label: "Quality", href: "#quality" },
    { label: "About", href: "#about" },
    { label: "Compliance", href: "#compliance" },
    { label: "Reviews", href: "#reviews" },
];

export function scrollToId(id) {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
                scrolled ? "bg-white/85 backdrop-blur-md border-slate-200 shadow-[0_8px_30px_rgba(15,23,42,0.06)]" : "bg-transparent border-transparent"
            }`}
        >
            <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                <div className="flex items-center justify-between h-16 md:h-20">
                    <button
                        data-testid="nav-logo-button"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="flex items-center gap-3 group"
                    >
                        <Logo className="w-9 h-9 transition-transform duration-500 group-hover:rotate-[30deg]" />
                        <span className="font-display font-bold text-lg tracking-tight text-slate-900">
                            Aus<span className="text-cyan-700">Research</span>
                        </span>
                    </button>

                    <nav className="hidden md:flex items-center gap-8">
                        {NAV.map((n) => (
                            <button
                                key={n.href}
                                data-testid={`nav-link-${n.label.toLowerCase()}`}
                                onClick={() => scrollToId(n.href)}
                                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors duration-300 relative after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-cyan-700 after:transition-[width] after:duration-300 hover:after:w-full"
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-4">
                        <span className="flex items-center gap-1.5 text-[11px] font-jbm uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
                            <ShieldAlert className="w-3.5 h-3.5" />
                            Research use only
                        </span>
                        <button
                            data-testid="nav-enquiry-button"
                            onClick={() => scrollToId("#enquiry")}
                            className="flex items-center gap-2 bg-slate-900 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-cyan-800 transition-colors duration-300"
                        >
                            <FlaskConical className="w-4 h-4" />
                            B2B Enquiry
                        </button>
                    </div>

                    <button
                        data-testid="mobile-menu-button"
                        className="md:hidden p-2 text-slate-800"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {open && (
                    <div className="md:hidden my-2 p-4 flex flex-col gap-1 bg-white border border-slate-200 rounded-2xl shadow-[0_24px_50px_-12px_rgba(15,23,42,0.25)]" data-testid="mobile-menu">
                        {NAV.map((n) => (
                            <button
                                key={n.href}
                                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                                onClick={() => { setOpen(false); scrollToId(n.href); }}
                                className="text-left text-base font-medium text-slate-700 px-3 py-3 rounded-xl hover:bg-slate-50 transition-colors"
                            >
                                {n.label}
                            </button>
                        ))}
                        <button
                            data-testid="mobile-nav-enquiry-button"
                            onClick={() => { setOpen(false); scrollToId("#enquiry"); }}
                            className="mt-2 flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-5 py-3.5 rounded-full"
                        >
                            <FlaskConical className="w-4 h-4" />
                            B2B Enquiry
                        </button>
                    </div>
                )}
            </div>
        </motion.header>
    );
}
